import React from 'react';
import { compose, omit, path, pick, prop } from 'ramda';
import { Image } from 'js/components/common/Image';
import { useLiveGameConfig, useGameImageUrls } from 'js/hooks/games';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { branch, mapProps, pure, renderNothing, withProps } from 'recompose';

import FeatureIcons from './FeatureIcons';
import { FavoriteIcon } from './FavoriteIcon';
import withGameLaunch from './withGameLaunch';
import { withGameInfo } from './withGameInfo';

import { useGameCodeLaunch } from '@interlayer/js/app/components/main/Games/hooks/useGameLaunch';

import style from './Game.scss';

export const GameBare = ({
    t,
    code,
    name,
    size = 'extraSmall',
    showTitle = false,
    showFeatureIcons = true,
    gameFeed,
    showFavoriteIcon,
    showRecentlyPlayedIcon,
    className = '',
    setGameRef,
    setImageRef,
    setGameImageRef,
    onMouseMove,
    isNew,
    hot,
    popular,
    exclusive,
    liveLaunchAlias,
    includeLiveGames = false
}) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = includeLiveGames && liveGameConfig.launchAlias;
    const { dynamicImageUrl, limits, isScheduled, openingDateTime, dealerName, language, seatsInfo } = liveGameConfig;
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });
    const gameLaunch = useGameCodeLaunch(code);

    return (
        <AccessibilityElement
            onClick={gameLaunch}
            data-game-code={code}
            elementRef={setGameRef}
            className={`${style.container} ${style[size]} ${className}`}
            aria-label={`${t('playtech.mobile.system.game.aria-label')} ${name}`}
        >
            <div
                aria-hidden={true}
                ref={setGameImageRef}
                onMouseMove={onMouseMove}
                className={style.imageContainer}
            >
                <Image
                    alt={name}
                    src={imageUrls}
                    ref={setImageRef}
                    className={style.image}
                />
                {isScheduledLiveGame && <div className={style.scheduledLiveGameBackground} />}
                {(showFeatureIcons || showFavoriteIcon) && <div className={style.background} />}
                {showRecentlyPlayedIcon && <div className={style.recentlyPlayedIcon} />}
            </div>
            <div className={`${style.sideLabels} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                {showFeatureIcons && (
                    <div className={style.labels}>
                        {isNew && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.new')}</span>}
                        {popular && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.popular')}</span>}
                        {hot && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.hot')}</span>}
                        {exclusive && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.exclusive')}</span>}
                    </div>
                )}
                {isLiveGame && limits && <div className={style.liveLimits}>{limits}</div>}
            </div>
            {isScheduledLiveGame && (
                <div className={`${style.scheduledLiveGameInfo} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                    {openingDateTime
                        ? (
                            <>
                                <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                <span>{openingDateTime}</span>
                            </>
                        )
                        : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                    }
                </div>
            )}
            <div className={`${style.sideIcons} ${showTitle ? style.withTitle : ''}`}>
                {isLiveGame && (
                    <div className={style.liveInfo} aria-hidden={true}>
                        <span>{dealerName}</span>
                        <span>{language}</span>

                        {showFeatureIcons && (
                            <FeatureIcons
                                code={code}
                                noContainer={true}
                                aria-hidden={true}
                                classNames={{ item: style.featured }}
                                types={['goldenChips', 'wilds', 'fsb']}
                            />
                        )}
                    </div>
                )}
                {showFeatureIcons && !isLiveGame && (
                    <>
                        <FeatureIcons
                            code={code}
                            tabIndex="0"
                            noContainer={true}
                            classNames={{ item: style.featured }}
                            types={['volatility']}
                        />
                        <FeatureIcons
                            code={code}
                            noContainer={true}
                            aria-hidden={true}
                            classNames={{ item: style.featured }}
                            types={['goldenChips', 'wilds', 'fsb']}
                        />
                    </>
                )}
                {!showTitle && showFavoriteIcon && !isLiveGame && (
                    <FavoriteIcon
                        code={code}
                        gameFeed={gameFeed}
                        className={style.favorite}
                    />
                )}
                {isLiveGame && (
                    <div className={style.seatsInfo} aria-hidden={true}>
                        <span>{seatsInfo}</span>
                        <span className={style.seatsIcon} />
                    </div>
                )}
            </div>
            {showTitle && (
                <div className={style.titleWrapper}>
                    <span className={`${style.name} ${style.ellipsis}`} aria-hidden={true}>{name}</span>
                    {showFavoriteIcon && (
                        <FavoriteIcon
                            code={code}
                            isFeatured={true}
                            gameFeed={gameFeed}
                            className={style.favorite}
                        />
                    )}
                </div>
            )}
        </AccessibilityElement>
    );
};

export const isGameAvailable = compose(
    withProps(({ code }) => ({ isAvailable: Boolean(useAppSelector(path(['games', code]))) })),
    branch(
        props => !props.isAvailable,
        renderNothing
    ),
    mapProps(omit(['isAvailable']))
);

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    branch(
        ({ showGameInfo }) => showGameInfo,
        withGameInfo,
        withGameLaunch
    ),
    withProps(({ code }) => ({
        isDesktop: useDeviceSelector(prop('isDesktop')),
        ...useAppSelector(path(['games', code])),
        ...useUserSelector(pick(['isLoggedIn']))
    })),
    mapProps(omit(['description', 'funSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);
